<template>
  <div>
    <phishing-reminder
      v-if="reminderDialog"
      :dialog="reminderDialog"
      @redirect="redirectToPhishing"
      @close="closeConfirmation"
      @neverAsk="neverAsk"
    />

    <div class="mt-5 pt-4">
      <nav
        class="mb-4"
        style="--bs-breadcrumb-divider: url(&#34;data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10'%3E%3Cpath d='M2.5 0L1 1.5 3.5 4 1 6.5 2.5 8l4-4-4-4z' fill='black'/%3E%3C/svg%3E&#34;);"
        aria-label="breadcrumb"
      >
        <ol class="breadcrumb">
          <li class="breadcrumb-item" style="font-size:24px; line-height: 24px; font-weight:900; color:#435666;">
            <strong>Dashboard</strong>
          </li>
          <li class="breadcrumb-item active" aria-current="page" style="font-size:24px; line-height: 24px; font-weight:300; color:#435666;">
            {{ this.managerFilter ? 'Managed Users view' : 'All Users view' }}
          </li>
        </ol>
      </nav>
    </div>

    <!-- Reusable Generic Widget Grid -->
    <generic-widget-grid
      v-if="!loading"
      v-model="layout"
      :default-layout="defaultLayout"
      :widgets="widgetsMeta"
      @save="onSaveLayout"
    >
      <!-- Custom Manager Toggle Button on Toolbar -->
      <template #toolbar-left>
        <button
          v-if="$store.state.auth.isManager && !this.$store.state.auth.isGeneralUser"
          class="btn-manager-toggle"
          @click="toggleManagerView()"
          v-tooltip.top="managerFilter ? 'Show All Users' : 'Show Managed Users'"
        >
          <i :class="managerFilter ? 'fas fa-user-cog me-1' : 'fas fa-users me-1'"></i>
          <span>{{ managerFilter ? 'Managed Users' : 'All Users' }}</span>
        </button>
      </template>

      <!-- Live Widget Component Slot -->
      <template #default="{ item }">
        <component
          :is="item.component"
          :organisationID="orgID"
          :key="dashboardKey"
          :showProduct="item.visible && item.showProduct"
          :isManager="managerFilter"
        />
      </template>
    </generic-widget-grid>
  </div>
</template>

<script>
import { VueScreenSizeMixin } from 'vue-screen-size';
import GenericWidgetGrid from '@/components/generic-components/GenericWidgetGrid.vue';
import Overdue from '@/components/dashboard/Overdue.vue';
import ClickThrough from '@/components/dashboard/ClickThrough.vue';
import PieSummary from '@/components/dashboard/PieSummary.vue';
import Ecc from '@/components/dashboard/EmailCompromiseCheck.vue';
import PentestSummary from '@/components/dashboard/PentestSummary.vue';
import PhishingReminder from '@/components/PhishingReminder.vue';
import AcceptanceReport from '@/components/policies/PolicyAcceptanceReport.vue';

export default {
  name: 'Dashboard',
  mixins: [VueScreenSizeMixin],

  components: {
    GenericWidgetGrid,
    PieSummary,
    PentestSummary,
    Overdue,
    Ecc,
    PhishingReminder,
    ClickThrough,
    AcceptanceReport
  },

  data() {
    return {
      loading: true,
      dashboardKey: 0,
      hasECC: false,
      hasTraining: false,
      hasPentest: false,
      hasPhishing: false,
      hasPolicy: false,
      vssWidth: 3000,
      reminderDialog: false,
      campaigns: [],
      managerFilter: this.$store.state.auth.isManager ? true : false,

      // Grid Layout State
      layout: [],
      defaultLayout: [
        { x: 0, y: 0, w: 3, h: 9.8, i: '0', static: false, component: 'PieSummary', condition: 'hasTraining', showProduct: true, visible: true },
        { x: 3, y: 0, w: 5, h: 13, i: '1', static: false, component: 'Overdue', condition: 'hasTraining', showProduct: true, visible: true },
        { x: 8, y: 0, w: 4, h: 16, i: '2', static: false, component: 'ClickThrough', condition: 'hasPhishing', showProduct: true, visible: true },
        { x: 0, y: 1, w: 4, h: 10, i: '3', static: false, component: 'Ecc', condition: 'hasECC', showProduct: true, visible: true },
        { x: 4, y: 1, w: 8, h: 18, i: '4', static: false, component: 'AcceptanceReport', condition: 'hasPolicy', showProduct: true, visible: true },
        { x: 0, y: 3, w: 12, h: 20, i: '6', static: false, component: 'PentestSummary', condition: 'hasPentest', showProduct: true, visible: true }
      ],

      // Widget Metadata Definition
      widgetsMeta: {
        PieSummary: {
          title: 'Training Summary Status',
          subtitle: 'Overview of user completion & progress',
          icon: 'fas fa-chart-pie',
          badge: 'Chart',
          color: '#00a0c1',
          bgSoft: '#e0f7fa',
          previewType: 'donut',
          minH: 8
        },
        Overdue: {
          title: 'Overdue Training',
          subtitle: 'Employees with delayed modules',
          icon: 'fas fa-user-clock',
          badge: 'Table',
          color: '#e11d48',
          bgSoft: '#ffe4e6',
          previewType: 'table',
          minH: 9
        },
        ClickThrough: {
          title: 'Click Throughs on Phishing Emails',
          subtitle: 'Simulated phishing test engagement metrics',
          icon: 'fas fa-fish',
          badge: 'Analytics',
          color: '#8b5cf6',
          bgSoft: '#f3e8ff',
          previewType: 'bars',
          minH: 10
        },
        Ecc: {
          title: 'Email Compromise Check (ECC)',
          subtitle: 'Email breaches and compromised accounts',
          icon: 'fas fa-shield-alt',
          badge: 'Security',
          color: '#f59e0b',
          bgSoft: '#fef3c7',
          previewType: 'security',
          minH: 8
        },
        AcceptanceReport: {
          title: 'Policy Acceptance Report',
          subtitle: 'Organisation policies compliance and sign-offs',
          icon: 'fas fa-file-signature',
          badge: 'Compliance',
          color: '#10b981',
          bgSoft: '#d1fae5',
          previewType: 'compliance',
          minH: 9
        },
        PentestSummary: {
          title: 'Penetration Testing Summary',
          subtitle: 'Vulnerability assessment and security findings',
          icon: 'fas fa-bug',
          badge: 'Security',
          color: '#6366f1',
          bgSoft: '#e0e7ff',
          previewType: 'pentest',
          minH: 10
        }
      }
    };
  },

  async mounted() {
    await this.init();
    this.vssWidth = this.$vssWidth;

    await this.getUserSettings();
    this.loading = false;

    if (!this.$store.state.auth.isSuperAdmin) {
      this.setPhishingReminder();
    }
  },

  methods: {
    getUserSettings() {
      return this.$axios
        .get('/user-settings/users/' + this.$store.state.auth.jwtPayload.user_id)
        .then((response) => {
          if (response.data.success) {
            if (!response.data.settings || !response.data.settings.grid_layout) {
              this.layout = this.updateConditions(this.defaultLayout);
              this.createUserPreference();
            } else {
              try {
                const gridLayout = JSON.parse(response.data.settings.grid_layout);

                if (!this.hasLayoutChanged(gridLayout, this.defaultLayout)) {
                  this.layout = this.updateConditions(gridLayout);
                } else {
                  this.layout = this.updateConditions(this.syncLayout(gridLayout, this.defaultLayout));
                }
              } catch (error) {
                console.error('Error parsing grid layout:', error);
                this.layout = this.updateConditions(this.defaultLayout);
              }
            }
          } else {
            this.layout = this.updateConditions(this.defaultLayout);
          }
        })
        .catch((error) => {
          console.error('Error fetching user settings:', error);
          this.layout = this.updateConditions(this.defaultLayout);
        });
    },

    onSaveLayout(newLayout) {
      this.layout = newLayout;
      this.updateUserSettings(true);
    },

    updateUserSettings(toNotify) {
      const updatedLayout = JSON.stringify(this.layout);

      const data = {
        user_id: this.$store.state.auth.jwtPayload.user_id,
        grid_layout: updatedLayout
      };

      return this.$axios
        .put('/user-settings/users/' + this.$store.state.auth.jwtPayload.user_id, data)
        .then((response) => {
          if (response.data.success) {
            if (toNotify) {
              this.$toast.success('User settings updated successfully.');
            }
          } else {
            if (toNotify) {
              this.$toast.error('Failed to update user settings.');
            }
          }
        })
        .catch((error) => {
          console.error('Error updating user settings:', error);
        });
    },

    hasLayoutChanged(savedLayout, defaultLayout) {
      if (savedLayout.length !== defaultLayout.length) return true;

      return defaultLayout.some((defaultItem) => {
        const savedItem = savedLayout.find((item) => item.i === defaultItem.i);

        if (!savedItem) return true;

        return (
          savedItem.h !== defaultItem.h ||
          savedItem.condition !== defaultItem.condition
        );
      });
    },

    syncLayout(savedLayout, defaultLayout) {
      return defaultLayout.map((defaultItem) => {
        const savedItem = savedLayout.find((item) => item.i === defaultItem.i);
        return savedItem
          ? { ...savedItem, h: defaultItem.h, condition: defaultItem.condition }
          : defaultItem;
      });
    },

    async createUserPreference() {
      const data = {
        user_id: this.$store.state.auth.jwtPayload.user_id,
        grid_layout: this.updateConditions(this.defaultLayout)
      };

      await this.$axios
        .post('/user-settings/users/' + this.$store.state.auth.jwtPayload.user_id, data)
        .then((response) => {
          if (!response.data.success) {
            this.$toast.error('Failed creating user settings.');
          }
        })
        .catch();
    },

    updateConditions(layout) {
      return layout.map((item) => ({
        ...item,
        showProduct: this.evaluateCondition(item.condition)
      }));
    },

    evaluateCondition(condition) {
      switch (condition) {
        case 'hasTraining':
          return this.hasTraining;
        case 'hasPhishing':
          return this.hasPhishing;
        case 'hasECC':
          return this.hasECC;
        case 'hasPentest':
          return this.hasPentest;
        case 'hasPolicy':
          return this.hasPolicy;
        default:
          return false;
      }
    },

    init() {
      if (this.$store.state.auth.isSuperAdmin || this.$helper.isVertexAnalyst()) {
        this.hasTraining = true;
        this.hasECC = true;
        this.hasPentest = true;
        this.hasPhishing = true;
        this.hasPolicy = true;
        return;
      }

      if (this.$store.state.auth.isPentester) {
        this.hasPentest = true;
      }

      if (this.$store.state.auth.isManager && this.managerFilter == true) {
        this.filterProducts();
        return;
      }

      this.setProductAccess();
    },

    filterProducts() {
      const products = this.$store.state.auth.orgProducts || [];

      for (const product of products) {
        if (!product || !product.product) continue;
        const productName = product.product.toLowerCase();

        if (productName === 'email compromise check') {
          this.hasECC = true;
        }

        if (productName === 'cyber security online awareness training') {
          this.hasTraining = true;
        }

        if (productName === 'automated test phishing campaigns') {
          this.hasPhishing = true;
        }

        if (productName === 'policy management') {
          this.hasPolicy = true;
        }
      }
    },

    setProductAccess() {
      this.hasECC = this.$store.state.auth.isECCAdmin;
      this.hasTraining = this.$store.state.auth.isTrainingAdmin;
      this.hasPentest = this.$store.state.auth.isPentestAdmin || this.$store.state.auth.isPentester;
      this.hasPhishing = this.$store.state.auth.isPhishingAdmin;
      this.hasPolicy = this.$store.state.auth.isPolicyAdmin;
    },

    resetProductAccess() {
      this.hasTraining = false;
      this.hasECC = false;
      this.hasPhishing = false;
      this.hasPentest = false;
      this.hasPolicy = false;
    },

    async getAutoCampaign() {
      if (this.$helper.isVertexAnalyst() || !this.$store.state.auth.isPhishingAdmin || !this.hasPhishing) {
        return;
      }

      await this.$axios
        .get('/orgadmin/organisations/' + this.orgID + '/organisation-campaign')
        .then((response) => {
          if (response.data.success) {
            this.campaigns = response.data.campaigns;
          } else {
            this.campaigns = [];
          }
        })
        .catch();
    },

    async setPhishingReminder() {
      if (this.$helper.isVertexAnalyst()) {
        let analystOrg = null;
        try {
          const organisationString = localStorage.getItem('userOrganisation');
          analystOrg = organisationString ? JSON.parse(organisationString) : null;
        } catch (e) {
          analystOrg = null;
        }
        const analystOrgId = analystOrg?.id || this.$store.state.auth.jwtPayload?.organisation_role?.[0]?.organisation_id;
        if (!analystOrgId || this.orgID != analystOrgId) {
          return;
        }
      }

      await this.getAutoCampaign();

      if (this.campaigns.length != 0 && this.campaigns[0].is_active == 1) return;

      if (localStorage.getItem('phishing_reminder') === null) {
        localStorage.setItem('phishing_reminder', 'empty');
      }

      const reminderValue = localStorage.getItem('phishing_reminder');
      if (reminderValue == false || reminderValue === 'false') return;

      const rawProducts = this.$store.state.user.organisation?.products;
      const products = this.$helper.isVertexAnalyst()
        ? (Array.isArray(rawProducts?.[0]) ? rawProducts[0] : (rawProducts?.product || rawProducts || []))
        : rawProducts;
      let hasPhishing = false;

      if (Array.isArray(products)) {
        for (const product of products) {
          if (product && product.product && product.product.toLowerCase() === 'automated_test_phishing_campaigns') {
            if (this.$store.state.auth.isOrganisationAdmin || this.$store.state.user.organisation?.is_organisation_admin) {
              hasPhishing = true;
              break;
            } else if (product.roles && product.roles.includes('admin')) {
              hasPhishing = true;
              break;
            }
          }
        }
      }

      if (!hasPhishing) return;

      if (reminderValue === 'empty') {
        const orgFeatures = await this.getOrganisationFeatures();
        if (orgFeatures != null && orgFeatures.length > 0) {
          const reminder = orgFeatures[0].phishing_reminder;
          switch (reminder) {
            case null:
              this.updateOrganisationFeature(true);
              localStorage.setItem('phishing_reminder', true);
              break;
            case true:
              localStorage.setItem('phishing_reminder', true);
              break;
            case false:
              localStorage.setItem('phishing_reminder', false);
              break;
          }
        }
      }

      if (reminderValue == true || reminderValue === 'true') {
        this.reminderDialog = true;
      }
    },

    async getOrganisationFeatures() {
      const data = await this.$axios
        .get(`/orgadmin/organisations/` + this.orgID + `/organisation-feature`)
        .then((response) => {
          if (response.data.success) {
            return response.data.org_features;
          } else {
            this.$toast.error(response.data.message);
            return null;
          }
        })
        .catch();

      return data;
    },

    async updateOrganisationFeature(value) {
      const data = {
        organisation_id: this.orgID,
        phishing_reminder: value
      };
      const orgFeature = await this.$axios.put(
        '/orgadmin/organisations/' + this.orgID + '/organisation-feature',
        data
      );
      return orgFeature;
    },

    redirectToPhishing() {
      this.$router.push({ name: 'PhishingCampaignSettings' });
      localStorage.setItem('phishing_reminder', false);
    },

    closeConfirmation() {
      localStorage.setItem('phishing_reminder', false);
      this.reminderDialog = false;
    },

    neverAsk() {
      this.updateOrganisationFeature(false);
      localStorage.setItem('phishing_reminder', false);
    },

    resetData() {
      this.loading = false;
    },

    async toggleManagerView() {
      if (this.$store.state.auth.isGeneralUser) {
        return;
      }

      this.managerFilter = !this.managerFilter;

      await this.resetProductAccess();
      await this.init();

      this.layout = this.updateConditions(this.layout);
      this.dashboardKey++;
    }
  },

  watch: {
    organisation() {
      if (this.$store.state.auth.accessToken == null) return;
      this.loading = true;
      this.resetData();
      this.dashboardKey += 1;
    },
    '$store.state.auth.orgProducts': {
      handler(newProducts) {
        if (newProducts && this.$store.state.auth.isManager && this.managerFilter == true) {
          this.filterProducts();
        }
      }
    }
  },

  computed: {
    organisation() {
      return this.$store.state.auth.isSuperAdmin || this.$helper.isVertexAnalyst()
        ? this.$store.state.user.organisation.name
        : this.$store.state.user.organisation.organisation;
    },

    orgID() {
      if (this.$helper.isVertexAnalyst()) {
        return this.$store.state.user.organisation.id;
      }
      return this.$helper.setOrganisationID();
    }
  }
};
</script>

<style scoped>
.btn-manager-toggle {
  display: inline-flex;
  align-items: center;
  background: #f8fafc;
  color: #435666;
  border: 1.5px solid #e2e8f0;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.875rem;
  padding: 7px 12px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-manager-toggle:hover {
  background: #f1f5f9;
  border-color: #cbd5e1;
  color: #1e293b;
}
</style>